import { useEffect, useRef, useState } from 'react';
import { Eye, MoreVertical, PanelRightOpen, Trash2, Users } from 'lucide-react';
import { GroupType } from '../../../types/groups';
import type { GroupDto } from '../../../types/groups';
import { useFieldLabel } from '../../../hooks/useFieldLabel';

interface GroupChatHeaderProps {
  group: GroupDto;
  onViewDetails: () => void;
  onDeleteGroup: () => void;
  onOpenMembers: () => void;
  showDeleteOption: boolean;
}

export default function GroupChatHeader({ group, onViewDetails, onDeleteGroup, onOpenMembers, showDeleteOption }: GroupChatHeaderProps) {
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const listLabel = useFieldLabel('MENU_LIST', 'List');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewDetails = () => {
    setIsMenuOpen(false);
    onViewDetails();
  };

  const handleDeleteGroup = () => {
    setIsMenuOpen(false);
    onDeleteGroup();
  };

  const groupName = group.groupName?.trim() || 'Untitled List';
  const groupType = group.groupType?.trim() || GroupType.Project;
  const memberCount = group.members?.length || 0;

  const groupTypeStyles: Record<string, string> = {
    [GroupType.Project]: 'bg-blue-50 text-blue-700 ring-blue-200',
    [GroupType.CustomerCall]: 'bg-purple-50 text-purple-700 ring-purple-200',
    [GroupType.Problem]: 'bg-orange-50 text-orange-700 ring-orange-200',
  };
  const groupTypeClass = groupTypeStyles[groupType] || 'bg-slate-100 text-slate-700 ring-slate-200';

  return (
    <header className="flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-100">
          <Users className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h1 className="mb-1 truncate text-lg font-bold text-gray-900 sm:text-xl">{groupName}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${groupTypeClass}`}>{groupType}</span>
            <span className="text-sm text-gray-600">
              {memberCount} {memberCount === 1 ? 'Member' : 'Members'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onOpenMembers}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900 lg:hidden"
          aria-label="Open members"
        >
          <PanelRightOpen className="h-5 w-5" />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setIsMenuOpen((current) => !current)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
            aria-label={`Open actions for ${groupName}`}
            aria-expanded={isMenuOpen}
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {isMenuOpen ? (
            <div className="absolute right-0 z-30 mt-2 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={handleViewDetails}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                View Details
              </button>
              {showDeleteOption &&(
              <button
                type="button"
                onClick={handleDeleteGroup}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete {listLabel}
              </button>)}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
